import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const err = (e) => e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Request failed";

export const VoiceSimulator = ({ onChange }) => {
  const [mobile, setMobile] = useState("");
  const [lang, setLang] = useState("en");
  const [call, setCall] = useState(null);
  const [lines, setLines] = useState([]);
  const [digits, setDigits] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const push = (who, msg) => setLines((l) => [...l, { who, msg, id: l.length }]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/voice/mock/calls", { caller_mobile: mobile, lang });
      setCall({ ...data, status: "active" }); setLines([]); push("aina", data.prompt); onChange?.();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const send = async (body, label) => {
    if (!call) return;
    setBusy(true); push("caller", label);
    try {
      const { data } = await api.post(`/admin/voice/mock/calls/${call.call_id}/input`, { secret: call.secret, ...body });
      setCall((c) => ({ ...c, ...data })); push("aina", data.prompt);
      onChange?.();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const hangup = async () => {
    try { const { data } = await api.post(`/admin/voice/mock/calls/${call.call_id}/hangup`, { secret: call.secret }); setCall((c) => ({ ...c, status: data.status })); onChange?.(); }
    catch (e) { toast.error(err(e)); }
  };
  const live = call && call.status === "active";

  return (
    <div className="min-w-0 rounded-2xl border border-stone-200 bg-white p-4 space-y-3" data-testid="voice-simulator">
      <div className="text-xs font-black uppercase tracking-widest text-stone-500">Simulated incoming call (MOCK)</div>
      <div className="flex flex-wrap gap-2">
        <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Caller mobile (10 digits)" inputMode="numeric"
               className="h-10 flex-1 min-w-[160px] rounded-xl border border-stone-200 px-3 text-sm" data-testid="voice-sim-mobile" />
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-10 rounded-xl border border-stone-200 px-2 text-sm" data-testid="voice-sim-lang">
          <option value="en">English</option><option value="hi">हिंदी</option>
        </select>
        <button onClick={start} disabled={busy || mobile.replace(/\D/g, "").length < 10} data-testid="voice-sim-start"
                className="h-10 px-4 rounded-full bg-stone-900 text-white text-sm font-bold disabled:opacity-40">Start MOCK call</button>
      </div>
      {call && (
        <div className="text-xs text-stone-500" data-testid="voice-sim-meta">
          {call.call_id} · caller {call.caller} · {call.identity} · lang {call.lang} · state {call.state} · <b data-testid="voice-sim-status">{call.status}</b>
        </div>
      )}
      <div className="max-h-72 overflow-y-auto space-y-2 rounded-xl bg-stone-50 p-3" data-testid="voice-sim-transcript">
        {lines.length === 0 && <div className="text-sm text-stone-400">No call yet.</div>}
        {lines.map((l) => (
          <div key={l.id} className={`text-sm ${l.who === "aina" ? "text-stone-800" : "text-sky-700 text-right"}`} data-testid={`voice-line-${l.id}`}>
            <span className="text-[10px] font-black uppercase mr-1">{l.who === "aina" ? "AINA · MOCK text" : "Caller"}</span>{l.msg}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 max-w-[240px]" data-testid="voice-sim-keypad">
        {KEYS.map((k) => (
          <button key={k} disabled={!live || busy} onClick={() => setDigits((d) => d + k)} data-testid={`voice-key-${k === "*" ? "star" : k === "#" ? "hash" : k}`}
                  className="h-11 rounded-xl border border-stone-300 font-black disabled:opacity-40">{k}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={digits} readOnly className="h-10 w-32 rounded-xl border border-stone-200 px-3 text-sm font-mono" data-testid="voice-sim-digits" />
        <button disabled={!live || busy || !digits} onClick={() => { send({ dtmf: digits }, `DTMF ${digits}`); setDigits(""); }} data-testid="voice-sim-send-dtmf"
                className="h-10 px-4 rounded-full bg-sky-700 text-white text-sm font-bold disabled:opacity-40">Send keys</button>
        <button disabled={!digits} onClick={() => setDigits("")} data-testid="voice-sim-clear" className="h-10 px-3 rounded-full border text-sm">Clear</button>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Typed 'speech' (MOCK — not speech recognition)"
               className="h-10 flex-1 min-w-[180px] rounded-xl border border-stone-200 px-3 text-sm" data-testid="voice-sim-text" />
        <button disabled={!live || busy || !text.trim()} onClick={() => { send({ text }, `MOCK typed: ${text}`); setText(""); }} data-testid="voice-sim-send-text"
                className="h-10 px-4 rounded-full bg-stone-700 text-white text-sm font-bold disabled:opacity-40">Send text</button>
        <button disabled={!live} onClick={hangup} data-testid="voice-sim-hangup" className="h-10 px-4 rounded-full border border-red-300 text-red-700 text-sm font-bold disabled:opacity-40">Hang up</button>
      </div>
    </div>
  );
};
